// ══════════════════════════════════════════════════════════════════════════════
// JOIN COLLAB PAGE - Page pour rejoindre une collaboration via token d'invitation
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, CheckCircle, XCircle, Loader2, ArrowRight, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function JoinCollabPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [collabInfo, setCollabInfo] = useState(null);

  // Vérifier la validité du token et récupérer les infos
  useEffect(() => {
    const checkInvite = async () => {
      try {
        const res = await fetch(`${API_URL}/api/collabs/invite/${token}/info`);
        if (res.ok) {
          const data = await res.json();
          setCollabInfo(data);
        } else {
          const err = await res.json();
          setError(err.detail || 'Invitation invalide ou expirée');
        }
      } catch (e) {
        setError('Impossible de vérifier l\'invitation');
      } finally {
        setLoading(false);
      }
    };
    
    if (token) checkInvite();
  }, [token]);

  // Rejoindre la collab
  const handleJoin = async () => {
    setJoining(true);
    try {
      const res = await fetch(`${API_URL}/api/collabs/join/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSuccess(true);
        toast.success('Bienvenue dans la collaboration !');
        
        // Stocker le token d'accès si fourni
        if (data.access_token) {
          localStorage.setItem('collab_token', data.access_token);
          localStorage.setItem('collab_user', JSON.stringify({
            name: collabInfo?.member_name,
            email: collabInfo?.member_email,
            is_external: true
          }));
        }
        
        // Rediriger vers la collab après 2s
        setTimeout(() => {
          navigate('/cockpit-pro/collaboration/collabs');
        }, 2000);
      } else {
        const err = await res.json();
        setError(err.detail || 'Impossible de rejoindre');
      }
    } catch (e) {
      setError('Erreur lors de la connexion');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <Loader2 size={48} style={styles.spinner} />
          <p style={styles.loadingText}>Vérification de l'invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          style={styles.card}
        >
          <div style={styles.iconError}>
            <XCircle size={48} />
          </div>
          <h1 style={styles.title}>Invitation invalide</h1>
          <p style={styles.error}>{error}</p>
          <button 
            style={styles.buttonSecondary}
            onClick={() => navigate('/')}
          >
            Retour à l'accueil
          </button>
        </motion.div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={styles.container}>
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          style={styles.card}
        >
          <div style={styles.iconSuccess}>
            <CheckCircle size={48} />
          </div>
          <h1 style={styles.title}>Bienvenue !</h1>
          <p style={styles.subtitle}>Tu as rejoint "{collabInfo?.collab_name}"</p>
          <p style={styles.redirectText}>Redirection en cours...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <motion.div 
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        style={styles.card}
      >
        {/* Header avec badge collab */}
        <div style={styles.collabBadge}>
          <div style={{ ...styles.collabIcon, background: collabInfo?.color || '#F97316' }}>
            {collabInfo?.collab_name?.[0]?.toUpperCase() || 'C'}
          </div>
        </div>
        
        <h1 style={styles.title}>Tu es invité à collaborer</h1>
        
        {/* Nom de la collab */}
        <div style={styles.collabName}>
          <Users size={20} style={{ color: '#64748B' }} />
          <span>{collabInfo?.collab_name}</span>
        </div>
        
        {/* Info inviteur */}
        <p style={styles.invitedBy}>
          Invité par <strong>{collabInfo?.invited_by_name}</strong>
        </p>
        
        {/* Description */}
        {collabInfo?.collab_description && (
          <p style={styles.description}>{collabInfo.collab_description}</p>
        )}
        
        {/* Message personnalisé */}
        {collabInfo?.invite_message && (
          <div style={styles.messageBox}>
            <Mail size={16} style={{ color: '#F59E0B', flexShrink: 0 }} />
            <p style={styles.message}>"{collabInfo.invite_message}"</p>
          </div>
        )}
        
        {/* Info utilisateur */}
        <div style={styles.userInfo}>
          <p>Tu rejoindras en tant que :</p>
          <div style={styles.userCard}>
            <div style={styles.userAvatar}>
              {collabInfo?.member_name?.[0]?.toUpperCase() || '?'}
            </div>
            <div>
              <p style={styles.userName}>{collabInfo?.member_name}</p>
              <p style={styles.userEmail}>{collabInfo?.member_email}</p>
            </div>
          </div>
        </div>
        
        {/* CTA */}
        <button 
          style={styles.buttonPrimary}
          onClick={handleJoin}
          disabled={joining}
          data-testid="join-collab-btn"
        >
          {joining ? (
            <>
              <Loader2 size={18} style={styles.spinnerSmall} />
              Connexion...
            </>
          ) : (
            <>
              Rejoindre la collaboration
              <ArrowRight size={18} />
            </>
          )}
        </button>
        
        <p style={styles.disclaimer}>
          En rejoignant, tu acceptes de partager ton activité avec les membres de cet espace.
        </p>
      </motion.div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1E3A5F 0%, #2d5a8f 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    background: '#FFFFFF',
    borderRadius: 24,
    padding: '48px 40px',
    maxWidth: 480,
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  collabBadge: {
    marginBottom: 24,
  },
  collabIcon: {
    width: 80,
    height: 80,
    borderRadius: 20,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 32,
    fontWeight: 700,
    color: '#FFFFFF',
    boxShadow: '0 8px 16px rgba(249, 115, 22, 0.3)',
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: '#1E293B',
    margin: '0 0 8px',
  },
  subtitle: {
    fontSize: 18,
    color: '#64748B',
    margin: '0 0 24px',
  },
  collabName: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 20px',
    background: '#F1F5F9',
    borderRadius: 12,
    fontSize: 18,
    fontWeight: 600,
    color: '#1E293B',
    marginBottom: 16,
  },
  invitedBy: {
    color: '#64748B',
    fontSize: 14,
    marginBottom: 20,
  },
  description: {
    color: '#475569',
    fontSize: 15,
    lineHeight: 1.6,
    marginBottom: 20,
    padding: '0 20px',
  },
  messageBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    background: '#FEF3C7',
    borderRadius: 12,
    marginBottom: 24,
    textAlign: 'left',
  },
  message: {
    margin: 0,
    color: '#92400E',
    fontSize: 14,
    fontStyle: 'italic',
    lineHeight: 1.5,
  },
  userInfo: {
    marginBottom: 24,
  },
  userCard: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 20px',
    background: '#F8FAFC',
    borderRadius: 12,
    marginTop: 8,
  },
  userAvatar: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#FFFFFF',
    fontWeight: 600,
    fontSize: 18,
  },
  userName: {
    margin: 0,
    fontWeight: 600,
    color: '#1E293B',
    textAlign: 'left',
  },
  userEmail: {
    margin: 0,
    fontSize: 13,
    color: '#64748B',
    textAlign: 'left',
  },
  buttonPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    padding: '16px 32px',
    background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 14,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(249, 115, 22, 0.4)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  buttonSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '14px 28px',
    background: '#F1F5F9',
    color: '#475569',
    border: 'none',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
    marginTop: 16,
  },
  disclaimer: {
    marginTop: 20,
    fontSize: 12,
    color: '#94A3B8',
  },
  iconSuccess: {
    color: '#22C55E',
    marginBottom: 16,
  },
  iconError: {
    color: '#EF4444',
    marginBottom: 16,
  },
  loadingText: {
    color: '#64748B',
    marginTop: 16,
  },
  redirectText: {
    color: '#64748B',
    fontSize: 14,
  },
  error: {
    color: '#DC2626',
    marginBottom: 20,
  },
  spinner: {
    animation: 'spin 1s linear infinite',
    color: '#F97316',
  },
  spinnerSmall: {
    animation: 'spin 1s linear infinite',
  },
};
